import { Moon, Sun } from 'lucide-react';
import type { Session } from '../api';
import { roleName } from '../account';
import { EmailSignatureCard } from '../components/settings/EmailSignatureCard';
import { PaymentOptionsCard } from '../components/settings/PaymentOptionsCard';
import { StripeCheckoutCard } from '../components/settings/StripeCheckoutCard';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PolicyPage } from './Policy';
import { Integrations } from './Integrations';
import { Observability } from './Observability';
import './settings.css';

const tabs = ['account', 'appearance', 'signature', 'policy', 'integrations', 'observability', 'demo'] as const;
type SettingsTab = typeof tabs[number];

/**
 * Workspace settings hub. Each tab shows its section in place, Policy and Integrations included, and the tab is in the
 * address (#settings/policy) so a link or Back lands on it. Notifications and Team stay out until they have real
 * controls (#36 / #235).
 */
export function Settings({ session, dark, onToggleTheme, tab: requested }: {
  session: Session;
  dark: boolean;
  onToggleTheme: () => void;
  tab?: string | null;
}) {
  const owner = session.actor.roles.includes('owner');
  const demo = session.connectors !== 'live';
  const role = session.actor.roles[0] ?? 'viewer';
  const name = session.userName?.trim() || 'Alder user';
  const shown = (t: SettingsTab) => (t === 'integrations' || t === 'observability' ? owner : t === 'demo' ? demo : true);
  const tab: SettingsTab = tabs.find(t => t === requested && shown(t)) ?? 'account';
  const choose = (next: string) => { window.location.hash = next === 'account' ? 'settings' : `settings/${next}`; };

  return (
    <main id="main-content" className="queue-page settings-page">
      <header className="page-heading">
        <div>
          <span className="eyebrow">YOUR WORKSPACE</span>
          <h1>Settings<span className="heading-dot">.</span></h1>
          <p>Account, appearance, how emails are signed, and how the agent is configured.</p>
        </div>
      </header>

      <Tabs value={tab} onValueChange={choose} className="settings-tabs">
        <TabsList variant="line" className="settings-tablist" aria-label="Settings sections">
          <TabsTrigger value="account">Account</TabsTrigger>
          <TabsTrigger value="appearance">Appearance</TabsTrigger>
          <TabsTrigger value="signature">Email signature</TabsTrigger>
          <TabsTrigger value="policy">Policy</TabsTrigger>
          {owner && <TabsTrigger value="integrations">Integrations</TabsTrigger>}
          {owner && <TabsTrigger value="observability">Observability</TabsTrigger>}
          {demo && <TabsTrigger value="demo">Demo</TabsTrigger>}
        </TabsList>

        <TabsContent value="account" className="settings-panel" id="settings-account">
          <dl className="settings-facts">
            <div><dt>Name</dt><dd>{name}</dd></div>
            <div><dt>Role</dt><dd>{roleName(role) || '—'}</dd></div>
            <div><dt>Company</dt><dd>{session.company ?? '—'}</dd></div>
          </dl>
          <p className="muted settings-note">To change your name or role, ask the owner of your workspace.</p>
        </TabsContent>

        <TabsContent value="appearance" className="settings-panel" id="settings-appearance">
          <div className="settings-row">
            <div>
              <strong>Appearance</strong>
              <p className="muted">Light or dark. Same preference as the account menu.</p>
            </div>
            <Button variant="outline" onClick={onToggleTheme} aria-pressed={dark}>
              {dark ? <Sun aria-hidden /> : <Moon aria-hidden />}
              {dark ? 'Use light' : 'Use dark'}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="signature" className="settings-panel" id="settings-signature">
          <EmailSignatureCard owner={owner} />
          <PaymentOptionsCard owner={owner} />
          <StripeCheckoutCard owner={owner} />
        </TabsContent>

        <TabsContent value="policy" className="settings-panel settings-panel-wide" id="settings-policy">
          {tab === 'policy' && <PolicyPage session={session} embedded />}
        </TabsContent>

        {owner && <TabsContent value="integrations" className="settings-panel settings-panel-wide" id="settings-integrations">
          {tab === 'integrations' && <Integrations session={session} embedded />}
        </TabsContent>}

        {owner && <TabsContent value="observability" className="settings-panel settings-panel-wide" id="settings-observability">
          {tab === 'observability' && <Observability id={null} embedded />}
        </TabsContent>}

        {demo && <TabsContent value="demo" className="settings-panel" id="settings-demo">
          <p className="muted">This host uses mock connectors. Stand-ins for the mailbox and Slack live here so they do not crowd the main nav.</p>
          <ul className="settings-links">
            <li><a href="#demo-mailbox">Demo mailbox</a></li>
            <li><a href="#demo-slack">Demo Slack</a></li>
          </ul>
        </TabsContent>}
      </Tabs>
    </main>
  );
}
